import React, { useId } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { SmallTertiaryButton } from '@ardoq/button';
import { text2 } from '@ardoq/typography';
import { colors, r8, s4, s8, s16 } from '@ardoq/design-tokens';
import { aiGradientBorderCSS } from '@ardoq/style-helpers';
import { ShimmerLabel } from '../shimmer-label';

export type AiLoadingLogoClickProps = {
  onClick: VoidFunction;
  label: string;
};

export type AiLoadingTextProps = {
  label: string;
  ariaLabel?: string;
  customButton?: React.ReactNode;
  onCancel?: VoidFunction;
  cancelLabel?: string;
  hasBorder?: boolean;
  className?: string;
  logoClickProps?: AiLoadingLogoClickProps;
};

export type AiLoadingConfig = {
  isLoading: boolean;
  label?: string;
  cancelButton?: {
    label: string;
    onClick: VoidFunction;
  };
};

const SQUARES_SPINNER_SIZE = 20;
const COLOR_DURATION = '12s';

const colorCycle = keyframes`
  0% { color: ${colors.orange500}; }
  25% { color: ${colors.pink500}; }
  50% { color: ${colors.purple50}; }
  75% { color: ${colors.purple35}; }
  100% { color: ${colors.orange500}; }
`;

const handleLogoKeyDown = (
  event: React.KeyboardEvent<HTMLDivElement>,
  onClick: VoidFunction
): void => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  onClick();
};

const SquaresSpinner = ({
  id,
  ariaLabel,
  logoClickProps,
}: {
  id: string;
  ariaLabel: string;
  logoClickProps?: AiLoadingLogoClickProps;
}) => (
  <SquaresSpinnerWrapper
    $id={id}
    $isClickable={logoClickProps !== undefined}
    role={logoClickProps && 'button'}
    tabIndex={logoClickProps && 0}
    aria-label={logoClickProps && ariaLabel}
    data-tooltip-text={logoClickProps?.label}
    onClick={logoClickProps?.onClick}
    onKeyDown={
      logoClickProps &&
      (event => handleLogoKeyDown(event, logoClickProps.onClick))
    }
  >
    <svg
      viewBox="0 0 400 400"
      aria-label={ariaLabel}
      aria-hidden={logoClickProps !== undefined}
      role="img"
    >
      <defs>
        <mask id={`loaderMask-${id}`} maskUnits="userSpaceOnUse">
          <rect x="0" y="0" width="400" height="400" fill="#000" />
          <g id={`rotator-${id}`}>
            <g id={`sq1-${id}`} className="moving-square">
              <rect className="mask-sq" x="0" y="0" width="45" height="45" />
            </g>
            <g id={`sq2-${id}`} className="moving-square">
              <rect
                className="mask-sq grow grow-up"
                x="0"
                y="0"
                width="45"
                height="45"
              />
            </g>
            <g id={`sq3-${id}`} className="moving-square">
              <rect className="mask-sq" x="0" y="0" width="45" height="45" />
            </g>
            <g id={`sq4-${id}`} className="moving-square">
              <rect
                className="mask-sq grow grow-right"
                x="0"
                y="0"
                width="45"
                height="45"
              />
            </g>
            <g id={`sq5-${id}`} className="moving-square">
              <rect className="mask-sq" x="0" y="0" width="45" height="45" />
            </g>
            <g id={`sq6-${id}`} className="moving-square">
              <rect
                className="mask-sq grow grow-down"
                x="0"
                y="0"
                width="45"
                height="45"
              />
            </g>
            <g id={`sq7-${id}`} className="moving-square">
              <rect className="mask-sq" x="0" y="0" width="45" height="45" />
            </g>
            <g id={`sq8-${id}`} className="moving-square">
              <rect
                className="mask-sq grow grow-left"
                x="0"
                y="0"
                width="45"
                height="45"
              />
            </g>
          </g>
        </mask>
      </defs>
      <rect
        x="0"
        y="0"
        width="400"
        height="400"
        fill="currentColor"
        mask={`url(#loaderMask-${id})`}
      />
    </svg>
  </SquaresSpinnerWrapper>
);

export const AiLoadingText = ({
  label,
  ariaLabel = 'Loading',
  customButton,
  onCancel,
  cancelLabel = 'Stop',
  hasBorder = true,
  className,
  logoClickProps,
}: AiLoadingTextProps) => {
  // useId can contain colons, strip them so the id is valid in css/svg selectors
  const id = useId().replace(/[^a-z0-9]/gi, '');
  const hasActions = onCancel !== undefined || customButton !== undefined;
  return (
    <Row className={className} $hasBorder={hasBorder}>
      <Content role="status">
        <SquaresSpinner
          id={id}
          ariaLabel={ariaLabel}
          logoClickProps={logoClickProps}
        />
        <LabelText>
          <ShimmerLabel label={label} />
        </LabelText>
      </Content>
      {hasActions && (
        <Actions>
          {onCancel && (
            <SmallTertiaryButton onClick={onCancel}>
              {cancelLabel}
            </SmallTertiaryButton>
          )}
          {customButton}
        </Actions>
      )}
    </Row>
  );
};

const Row = styled.div<{ $hasBorder: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${s8};
  width: 100%;

  ${({ $hasBorder }) =>
    $hasBorder &&
    css`
      position: relative;
      padding: ${s8};
      border-radius: ${r8};
      ${aiGradientBorderCSS}
      &::before {
        border-radius: ${r8};
      }
    `}
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${s4};
  flex-shrink: 0;
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  padding-top: ${s4};
  padding-bottom: ${s4};
  gap: ${s8};
  color: ${colors.orange500};
  animation: ${colorCycle} ${COLOR_DURATION} linear infinite;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const LabelText = styled.span`
  ${text2};
`;

const SquaresSpinnerWrapper = styled.div<{
  $id: string;
  $isClickable: boolean;
}>`
  position: relative;
  width: ${s16};
  height: ${s16};
  flex-shrink: 0;
  overflow: visible;

  ${({ $isClickable }) =>
    $isClickable &&
    css`
      cursor: pointer;
    `}

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${SQUARES_SPINNER_SIZE}px;
    height: ${SQUARES_SPINNER_SIZE}px;
    margin-top: -${SQUARES_SPINNER_SIZE / 2}px;
    margin-left: -${SQUARES_SPINNER_SIZE / 2}px;
  }

  #rotator-${({ $id }) => $id} {
    transform-box: view-box;
    transform-origin: 53% 53%;
    animation: rotateAll 9s cubic-bezier(0.19, 1, 0.22, 1) infinite;
  }

  @keyframes rotateAll {
    0%,
    40% {
      transform: rotate(0deg);
    }
    55%,
    65% {
      transform: rotate(360deg);
    }
    85%,
    100% {
      transform: rotate(0deg);
    }
  }

  .moving-square {
    transform-box: fill-box;
    transform-origin: 0 0;
    animation-duration: 9s;
    animation-iteration-count: infinite;
    animation-timing-function: cubic-bezier(0.18, 0.82, 0.33, 1);
  }

  .mask-sq {
    fill: ${colors.white950};
    stroke: ${colors.white950};
    stroke-width: 1.25px;
  }

  .grow {
    transform-box: fill-box;
    animation-duration: 9s;
    animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
    animation-iteration-count: infinite;
  }

  .grow-down {
    animation-name: growDown;
  }

  .grow-right {
    animation-name: growRight;
  }

  .grow-up {
    animation-name: growUp;
  }

  .grow-left {
    animation-name: growLeft;
  }

  @keyframes growDown {
    0%,
    40% {
      height: 45px;
    }
    55%,
    70% {
      height: 67.5px;
    }
    90%,
    100% {
      height: 45px;
    }
  }

  @keyframes growRight {
    0%,
    40% {
      width: 45px;
    }
    55%,
    70% {
      width: 67.5px;
    }
    90%,
    100% {
      width: 45px;
    }
  }

  @keyframes growUp {
    0%,
    40% {
      height: 45px;
      y: 0px;
    }
    55%,
    70% {
      height: 67.5px;
      y: -22.5px;
    }
    90%,
    100% {
      height: 45px;
      y: 0px;
    }
  }

  @keyframes growLeft {
    0%,
    40% {
      width: 45px;
      x: 0px;
    }
    55%,
    70% {
      width: 67.5px;
      x: -22.5px;
    }
    90%,
    100% {
      width: 45px;
      x: 0px;
    }
  }

  #sq1-${({ $id }) => $id} {
    transform: translate(147.09px, 140.13px);
  }

  #sq2-${({ $id }) => $id} {
    animation-name: sq2Anim-${({ $id }) => $id};
  }

  @keyframes sq2Anim-${({ $id }) => $id} {
    0%,
    7.5% {
      transform: translate(147.09px, 140.13px);
    }
    12.5%,
    60%,
    97.5% {
      transform: translate(193px, 94.5px);
    }
    99.5%,
    100% {
      transform: translate(147.09px, 140.13px);
    }
  }

  #sq3-${({ $id }) => $id} {
    animation-name: sq3Anim-${({ $id }) => $id};
  }

  @keyframes sq3Anim-${({ $id }) => $id} {
    0%,
    7.5% {
      transform: translate(147.09px, 140.13px);
    }
    12.5% {
      transform: translate(193px, 94.5px);
    }
    17.5%,
    60%,
    92.5% {
      transform: translate(238.5px, 140.13px);
    }
    97.5% {
      transform: translate(193px, 94.5px);
    }
    99.5%,
    100% {
      transform: translate(147.09px, 140.13px);
    }
  }

  #sq4-${({ $id }) => $id} {
    animation-name: sq4Anim-${({ $id }) => $id};
  }

  @keyframes sq4Anim-${({ $id }) => $id} {
    0%,
    7.5% {
      transform: translate(147.09px, 140.13px);
    }
    12.5% {
      transform: translate(193px, 94.5px);
    }
    17.5% {
      transform: translate(238.5px, 140.13px);
    }
    22.5%,
    60%,
    87.5% {
      transform: translate(284px, 185.5px);
    }
    92.5% {
      transform: translate(238.5px, 140.13px);
    }
    97.5% {
      transform: translate(193px, 94.5px);
    }
    99.5%,
    100% {
      transform: translate(147.09px, 140.13px);
    }
  }

  #sq5-${({ $id }) => $id} {
    transform: translate(147px, 231.5px);
  }

  #sq6-${({ $id }) => $id} {
    animation-name: sq6Anim-${({ $id }) => $id};
  }

  @keyframes sq6Anim-${({ $id }) => $id} {
    0%,
    22.5% {
      transform: translate(192px, 231.5px);
    }
    27.5%,
    60%,
    82.5% {
      transform: translate(192px, 277px);
    }
    87.5%,
    100% {
      transform: translate(192px, 231.5px);
    }
  }

  #sq7-${({ $id }) => $id} {
    animation-name: sq7Anim-${({ $id }) => $id};
  }

  @keyframes sq7Anim-${({ $id }) => $id} {
    0%,
    22.5% {
      transform: translate(233px, 231.5px);
    }
    27.5%,
    60%,
    82.5% {
      transform: translate(238.5px, 231.5px);
    }
    87.5%,
    100% {
      transform: translate(233px, 231.5px);
    }
  }

  #sq8-${({ $id }) => $id} {
    animation-name: sq8Anim-${({ $id }) => $id};
  }

  @keyframes sq8Anim-${({ $id }) => $id} {
    0%,
    27.5% {
      transform: translate(147px, 231.5px);
    }
    32.5%,
    60%,
    77.5% {
      transform: translate(101.5px, 186px);
    }
    82.5%,
    100% {
      transform: translate(147px, 231.5px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    & * {
      animation: none !important;
    }
  }
`;
